import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';

import { useAlertHook } from 'app/hooks/useAlertHook';
import { Button, Modal } from 'app/components';
import { RuleForAVessel, RuleForAPort, RuleForAnArea } from './ModalContent';
import { useCreateNotificationRuleMutation } from 'app/api/facade';
import { getSchema } from './Schemas';

import styles from './CreateNotificationModal.module.scss';
import { AccessorAndParent } from '../NotificationsPage';
import { t } from 'i18next';
import { ComponetnSettingT } from '../types';

type RuleSettingsContentT = {
  onClose: () => void;
  settings: ComponetnSettingT;
};

const RuleSettingsContent = ({ onClose, settings }: RuleSettingsContentT) => {
  const { t } = useTranslation();
  const { addAlert } = useAlertHook();

  const [isAnyChecked, setIsAnyChecked] = useState<boolean>(true);
  const [isFleetChecked, setIsFleetChecked] = useState<boolean>(true);

  const [createNotification, { ...createNotificationResponse }] = useCreateNotificationRuleMutation({});

  const getActorKey = (settings: ComponetnSettingT) => {
    if (settings.parent === AccessorAndParent.Area || settings.accessor === AccessorAndParent.Area) {
      return formik.values['area'].length === 0 ? [] : [formik.values['area']];
    }
    return formik.values['port'].length === 0 ? [] : [formik.values['port']];
  };

  const formikInit = {
    mmsi: '',
    port: '',
    area: '',
    any: true,
    isItemSelected: false,
    isFleetChecked: true,
    notificationChannels: [] as string[],
  };

  const handleClose = () => {
    formik.resetForm();
    onClose();
    setIsAnyChecked(true);
    setIsFleetChecked(true);
  };

  const formik = useFormik({
    initialValues: formikInit,
    enableReinitialize: true,
    validationSchema: settings.parent && settings.accessor && getSchema(settings.parent, settings.accessor),
    validateOnBlur: true,
    validateOnChange: false,
    onSubmit: (values, { resetForm }) => {
      if (settings.type === 'ANCHORAGE_ALL') {
        createNotification({
          eventType: 'ANCHORAGE_LOWER_THE_ANCHOR',
          active: true,
          actors: [
            {
              actorType: 'MMSI',
              actorKeys: values.mmsi !== null && values.mmsi.length > 0 ? [values.mmsi] : [],
            },
            {
              actorType: 'ANCHORAGE_KEY',
              actorKeys: [],
            },
          ],
          notificationChannels: formik.values.notificationChannels,
        }).then((response: any) => {
          if (response.error) {
            const data = response.error.data as any;
            addAlert({
              data: {
                title: data.title,
                status: data.status,
                detail: data.detail,
                messages: [
                  {
                    description: data.detail,
                    type: 'Error',
                  },
                ],
              },
            });
          }
          if (response.data) {
            addAlert({
              data: {
                title: 'Success',
                status: 200,
                detail: 'Success',
                messages: [{ description: t('ChangesSaved'), type: 'information' }],
              },
            });
            formik.setValues(formik.initialValues);
            handleClose();
          }
        });
        createNotification({
          eventType: 'ANCHORAGE_RAISE_THE_ANCHOR',
          notificationChannels: formik.values.notificationChannels,
          active: true,
          actors: [
            {
              actorType: 'MMSI',
              actorKeys: values.mmsi !== null && values.mmsi.length > 0 ? [values.mmsi] : [],
            },
            {
              actorType: 'ANCHORAGE_KEY',
              actorKeys: [],
            },
          ],
        }).then((response: any) => {
          if (response.error) {
            const data = response.error.data as any;
            addAlert({
              data: {
                title: data.title,
                status: data.status,
                detail: data.detail,
                messages: [
                  {
                    description: data.detail,
                    type: 'Error',
                  },
                ],
              },
            });
          }
          if (response.data) {
            addAlert({
              data: {
                title: 'Success',
                status: 200,
                detail: 'Success',
                messages: [{ description: t('ChangesSaved'), type: 'information' }],
              },
            });
            formik.setValues(formik.initialValues);
            handleClose();
          }
        });
      } else {
        const actors = [
          {
            actorType: 'MMSI',
            actorKeys:
              (settings.type === 'TARGET_SAFETY' && isFleetChecked) || values.mmsi === null || values.mmsi.length === 0
                ? []
                : [values.mmsi],
            specialOption: settings.accessor === AccessorAndParent.TargetSafety && isFleetChecked ? 'USER_FLEET' : null,
          },
        ];

        const additionalActor: any = {
          actorType:
            settings.parent === AccessorAndParent.Area || settings.accessor === AccessorAndParent.Area ? 'AREA_KEY' : 'PORT_KEY',
          actorKeys: getActorKey(settings),
        };

        if (settings.accessor !== AccessorAndParent.TargetSafety) {
          actors.push(additionalActor);
        }
        createNotification({
          eventType: settings.type,
          notificationChannels: formik.values.notificationChannels,
          active: true,
          actors: actors,
        }).then((response: any) => {
          if (response.error) {
            const data = response.error.data as any;
            addAlert({
              data: {
                title: data.title,
                status: data.status,
                detail: data.detail,
                messages: [
                  {
                    description: data.detail,
                    type: 'Error',
                  },
                ],
              },
            });
          }
          if (response.data) {
            addAlert({
              data: {
                title: 'Success',
                status: 200,
                detail: 'Success',
                messages: [{ description: t('ChangesSaved'), type: 'information' }],
              },
            });
            resetForm();
            handleClose();
          }
        });
      }
    },
  });

  useEffect(() => {
    if (settings.parent === 'port' && isAnyChecked) {
      formik.setFieldValue('mmsi', '');
    }
    isAnyChecked ? formik.setFieldValue('any', true) : formik.setFieldValue('any', false);
  }, [isAnyChecked]);

  useEffect(() => {
    formik.setFieldValue('isFleetChecked', isFleetChecked);
  }, [isFleetChecked]);


  return (
    <form className={styles.rows} onSubmit={formik.handleSubmit}>
      <div className={styles.row}>
        {settings.parent === AccessorAndParent.Vessel && (
          <RuleForAVessel
            settings={settings}
            onMmsiChange={formik.handleChange}
            onComboBoxChange={formik.setFieldValue}
            errors={formik.errors}
            isAnyCheckedState={[isAnyChecked, setIsAnyChecked]}
            isFleetCheckedState={[isFleetChecked, setIsFleetChecked]}
            isSuccess={createNotificationResponse.isSuccess}
          />
        )}

        {settings.parent === AccessorAndParent.Port && (
          <RuleForAPort
            settings={settings}
            onMmsiChange={formik.handleChange}
            onPortChange={formik.setFieldValue}
            errors={formik.errors}
            isAnyCheckedState={[isAnyChecked, setIsAnyChecked]}
            isFleetCheckedState={[isFleetChecked, setIsFleetChecked]}
            isSuccess={createNotificationResponse.isSuccess}
          />
        )}
        {settings.parent === AccessorAndParent.Area && (
          <RuleForAnArea
            settings={settings}
            onMmsiChange={formik.handleChange}
            onAreaChange={formik.setFieldValue}
            errors={formik.errors}
            isAnyCheckedState={[isAnyChecked, setIsAnyChecked]}
            isFleetCheckedState={[isFleetChecked, setIsFleetChecked]}
            isSuccess={createNotificationResponse.isSuccess}
          />
        )}
      </div>
      <div className={styles.button_group}>
        <Button variant="general" label={t('ButtonLabelDiscard')} type="button" onClick={() => handleClose()} />
        <Button variant="general" label={t('ButtonLabelCreate')} type="submit" />
      </div>
    </form>
  );
};

export const CreateVesselRulesModal = ({ showCreateVesselArrivalPortRuleState }: any) => {
  const [ruleSettings, setRuleSettings] = showCreateVesselArrivalPortRuleState;
  return (
    <Modal
      closeByClickOutside={false}
      onClose={() => setRuleSettings({ show: false, type: '', accessor: null, parent: null })}
      headerTitle={t('NotificationSettings')}
      isShown={ruleSettings.show}
      component={
        <RuleSettingsContent
          onClose={() =>
            setRuleSettings({
              show: false,
              type: '',
              accessor: null,
              parent: null,
            })
          }
          settings={ruleSettings}
        />
      }
      wrapperClass={styles.rule_modal}
    />
  );
};
